import React, { Component, ErrorInfo, ReactNode } from 'react';
import { WBBox, WBTypography, WBButton, WBAlert } from '@admiin-com/ds-web';

interface Props {
  children?: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    this.setState({
      error,
      errorInfo,
    });
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <WBBox
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            minHeight: '50vh',
            p: 3,
            textAlign: 'center',
          }}
        >
          <WBAlert severity="error" sx={{ mb: 3, maxWidth: 600 }}>
            <WBTypography variant="h6" sx={{ mb: 1 }}>
              Something went wrong
            </WBTypography>
            <WBTypography variant="body2" sx={{ mb: 2 }}>
              {this.state.error?.message || 'An unexpected error occurred'}
            </WBTypography>
          </WBAlert>

          <WBBox sx={{ display: 'flex', gap: 2 }}>
            <WBButton variant="contained" onClick={this.handleReload}>
              Reload Page
            </WBButton>
            <WBButton variant="outlined" onClick={this.handleReset}>
              Try Again
            </WBButton>
          </WBBox>

          {process.env.NODE_ENV === 'development' && this.state.errorInfo && (
            <WBBox
              sx={{
                mt: 3,
                p: 2,
                bgcolor: 'grey.100',
                borderRadius: 1,
                maxWidth: '100%',
                overflow: 'auto',
              }}
            >
              <WBTypography variant="caption" component="pre">
                {this.state.error?.stack}
                {this.state.errorInfo.componentStack}
              </WBTypography>
            </WBBox>
          )}
        </WBBox>
      );
    }

    return this.props.children;
  }
}
